import React from "react";

import { LeafIcon as Leaf } from "@packages/ds";
import { FormattedMessage } from "react-intl";

import { INFRASTRUCTURE_TYPE } from "Constants/constants";
import * as Accordion from "ds/Accordion";
import Button from "ds/Button";
import { Group } from "ds/Radio";
import { Tooltip } from "ds/Tooltip";
import { selectFeatureUsageBasedBillingEnabled } from "Reducers/featureFlags/featureFlags.selectors";
import { subscriptionPlanSortCallback } from "Reducers/organization/subscription";
import { selectFormattedRegions } from "Reducers/project/region";
import { useAppSelector } from "Store/hooks";

import { greenLeafColor } from "../ProjectCard/useProjectRegion";

import * as S from "./Filters.styles";

import type { Organization } from "@packages/client";

type Props = {
  organization: Organization;
  infrastructure?: string;
  setInfrastructure: (value?: string) => void;
  regions?: Array<string>;
  setRegions: (value?: Array<string>) => void;
  allPlans?: Record<string, string>;
  plans?: Array<{ id: string; label: string }>;
  setPlans: (value?: Array<{ id: string; label: string }>) => void;
  reset: () => void;
};

export const Filters = (props: Props) => {
  const allRegions = useAppSelector(selectFormattedRegions);

  const usageBasedPagesEnabled = useAppSelector(
    selectFeatureUsageBasedBillingEnabled
  );

  const isDedicatedSelected =
    props.infrastructure === INFRASTRUCTURE_TYPE.DEDICATED;

  const onRegionsChanged = (regionId: string) => () => {
    if (!props.regions) {
      props.setRegions([regionId]);
    } else if (props.regions.indexOf(regionId) >= 0) {
      props.setRegions(props.regions.filter(r => r !== regionId));
    } else {
      props.setRegions([...props.regions, regionId]);
    }
  };

  const onPlansChanged = (plan: { id: string; label: string }) => () => {
    if (!props.plans) {
      props.setPlans([plan]);
    } else if (props.plans.findIndex(p => p.id === plan.id) >= 0) {
      props.setPlans(props.plans.filter(p => p.id !== plan.id));
    } else {
      props.setPlans([...props.plans, plan]);
    }
  };

  return (
    <S.FilterSection>
      <S.FilterHeader>
        <h4>
          <FormattedMessage id="projects_filters_title" />
        </h4>
        <Button
          variant="link"
          onClick={props.reset}
          analyticId="projects_filters_clear"
        >
          <FormattedMessage id="projects_filters_clear" />
        </Button>
      </S.FilterHeader>
      <Accordion.Root type="single" defaultValue="type" collapsible>
        <Accordion.Item value="type">
          <Accordion.ListHeader>
            <FormattedMessage id="projects_filters_infrastructure" />
          </Accordion.ListHeader>
          <Accordion.ListContent>
            <Group value={props.infrastructure}>
              <S.InputInfrastructure
                id="grid"
                label={<FormattedMessage id="grid" />}
                onClick={() => {
                  props.setInfrastructure(INFRASTRUCTURE_TYPE.GRID);
                }}
                value={INFRASTRUCTURE_TYPE.GRID}
              />

              <S.InputInfrastructure
                id="dedicated"
                label={<FormattedMessage id="dedicated" />}
                onClick={() => {
                  props.setInfrastructure(INFRASTRUCTURE_TYPE.DEDICATED);
                }}
                value={INFRASTRUCTURE_TYPE.DEDICATED}
              />
            </Group>
          </Accordion.ListContent>
        </Accordion.Item>

        <Accordion.Item disabled={isDedicatedSelected} value="region">
          <Tooltip.Provider>
            <div>
              <Tooltip.Root>
                <Tooltip.Trigger>
                  <Accordion.ListHeader>
                    <S.Fake disabled={isDedicatedSelected}>
                      <FormattedMessage id="projects_filters_region" />{" "}
                      <S.FilterNumber>
                        {!!props.regions?.length && `(${props.regions.length})`}
                      </S.FilterNumber>
                    </S.Fake>
                  </Accordion.ListHeader>
                </Tooltip.Trigger>
                <S.DedicatedContent
                  align="start"
                  disabled={!isDedicatedSelected}
                >
                  <FormattedMessage id="projects_filters_region_tooltip" />
                  <Tooltip.Arrow />
                </S.DedicatedContent>
              </Tooltip.Root>
            </div>
          </Tooltip.Provider>

          <Accordion.ListContent>
            {allRegions.map(region => (
              <S.RegionBox key={region.id}>
                {" "}
                <S.ListCheckboxItem
                  checked={
                    props.regions
                      ? props.regions.indexOf(region.id) >= 0
                      : false
                  }
                  forId={region.id}
                  id={region.id}
                  label={region.label}
                  key={region.id}
                  onCheckedChange={onRegionsChanged(region.id)}
                />
                {region?.environmental_impact?.green && (
                  <S.Icon>
                    <Leaf
                      fill={`var(${greenLeafColor})`}
                      width={24}
                      height={24}
                    />
                  </S.Icon>
                )}
              </S.RegionBox>
            ))}
          </Accordion.ListContent>
        </Accordion.Item>

        {!usageBasedPagesEnabled && props.allPlans && (
          <Accordion.Item disabled={isDedicatedSelected} value="plan">
            <Tooltip.Provider>
              <div>
                <Tooltip.Root>
                  <Tooltip.Trigger>
                    <Accordion.ListHeader>
                      <S.Fake disabled={isDedicatedSelected}>
                        <FormattedMessage id="projects_filters_plan_size" />{" "}
                        <S.FilterNumber>
                          {!!props.plans?.length && `(${props.plans.length})`}
                        </S.FilterNumber>
                      </S.Fake>
                    </Accordion.ListHeader>
                  </Tooltip.Trigger>
                  <S.DedicatedContent
                    align="start"
                    disabled={!isDedicatedSelected}
                  >
                    <FormattedMessage id="projects_filters_plan_tooltip" />
                    <Tooltip.Arrow />
                  </S.DedicatedContent>
                </Tooltip.Root>
              </div>
            </Tooltip.Provider>

            <Accordion.ListContent>
              {Object.entries(props.allPlans)
                .map(([id, label]) => ({
                  id,
                  label
                }))
                .sort((a, b) => subscriptionPlanSortCallback(a.id, b.id))

                .map(plan => (
                  <S.ListCheckboxItem
                    checked={
                      props.plans
                        ? props.plans.findIndex(p => p.id == plan.id) >= 0
                        : false
                    }
                    forId={plan.id}
                    label={plan.label}
                    key={plan.id}
                    onCheckedChange={onPlansChanged(plan)}
                  />
                ))}
            </Accordion.ListContent>
          </Accordion.Item>
        )}
      </Accordion.Root>
    </S.FilterSection>
  );
};
